import type { AccountEventView, JsonValue } from "../../../shared/contracts.js";
import { ROLES, type Role } from "../../../shared/domain.js";
import { roleLabel } from "../cases/parts-actions/RecipientWarning.js";

/**
 * The words behind "Recent changes" on `/accounts`: one plain sentence per
 * account-history entry, naming the account (a rename leads with the name
 * the person had at the time, then the name they were given) and — for
 * creations and role changes — the roles by their labels. Pure, so the page renders whatever
 * the server recorded without a second source of truth about roles. An
 * event type the page does not know yet (the column is free text; a later
 * server may record one first) still reads as a sentence, never as a blank
 * line.
 */

/**
 * The label shown for an entry with no acting person — the deployment's
 * bootstrap wrote it. The same word the case activity timeline uses for its
 * actor-less events, so one concept has one name across the product.
 */
export const SYSTEM_ACTOR_LABEL = "System";

/** The `next` of the `role` change, as a label; "—" when the entry carries none. */
function roleFrom(event: AccountEventView, side: "previous" | "next"): string {
  const change = event.changes.find((candidate) => candidate.field === "role");
  return roleValueLabel(change ? change[side] : null);
}

function roleValueLabel(value: JsonValue): string {
  if (typeof value !== "string" || value === "") return "—";
  return (ROLES as readonly string[]).includes(value) ? roleLabel(value as Role) : value;
}

/**
 * The rename's two names as stored — `accountName` is the name *now*, so an
 * older rename takes both from the change rather than from the view — or
 * null when the entry carries none (the server always records both; a
 * malformed row), so the sentence can drop the "to …" half rather than
 * claim a name was renamed to itself.
 */
function renameFrom(event: AccountEventView): { previous: string; next: string } | null {
  const change = event.changes.find((candidate) => candidate.field === "displayName");
  if (!change) return null;
  const { previous, next } = change;
  if (typeof previous !== "string" || previous === "") return null;
  if (typeof next !== "string" || next === "") return null;
  return { previous, next };
}

/** `email_changed` → "email changed": the sentence voice of the known types. */
function plainEventType(eventType: string): string {
  const words = eventType.replace(/[_-]+/g, " ").trim();
  return words || eventType;
}

export function describeAccountEvent(event: AccountEventView): string {
  const name = event.accountName;
  switch (event.eventType) {
    case "account_created":
      return `${name} — account created as ${roleFrom(event, "next")}`;
    case "role_changed":
      return `${name} — role changed from ${roleFrom(event, "previous")} to ${roleFrom(event, "next")}`;
    case "name_changed": {
      const rename = renameFrom(event);
      return rename ? `${rename.previous} — renamed to ${rename.next}` : `${name} — renamed`;
    }
    case "account_deactivated":
      return `${name} — account deactivated`;
    case "account_reactivated":
      return `${name} — account reactivated`;
    case "password_reset":
      return `${name} — password reset`;
    case "administration_granted":
      return `${name} — made an administrator`;
    case "administration_revoked":
      return `${name} — administration removed`;
    // The temporary-password email. The view carries no metadata, so the
    // address is not here; the sentence names the email by what it is for.
    case "invitation_sent":
      return `${name} — sign-in email sent`;
    case "invitation_failed":
      return `${name} — sign-in email failed`;
    default:
      return `${name} — ${plainEventType(String(event.eventType satisfies never))}`;
  }
}

/** Who made the change: the administrator's name, or the system. */
export function actorLabel(event: AccountEventView): string {
  return event.actorName ?? SYSTEM_ACTOR_LABEL;
}
